//
// Copyright © 2020 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { Analytics } from '@hcengineering/analytics'
import '@hcengineering/platform-rig/profiles/ui/svelte'
import { derived, writable } from 'svelte/store'
import { setDateLanguage } from './dates'
import { ThemeVariant, type ThemeVariantType } from './variants'

export { setDateLanguage } from './dates'
export { default as Theme } from './Theme.svelte'
export { default as InvertedTheme } from './InvertedTheme.svelte'
export { ThemeVariant, type ThemeVariantType } from './variants'

/**
 * @public
 */
export const setDefaultLanguage = (language: string): void => {
  if (localStorage.getItem('lang') === null) {
    localStorage.setItem('lang', language)
  }
}

function getDefaultProps (prop: string, value: string): string {
  localStorage.setItem(prop, value)
  return value
}

/**
 * @public
 */
export const isSystemThemeDark = (): boolean => window.matchMedia('(prefers-color-scheme: dark)').matches
/**
 * @public
 */
export const isThemeDark = (theme: string): boolean =>
  theme === 'theme-dark' || (theme === 'theme-system' && isSystemThemeDark())
const lightDefaultKey = 'theme-light-default'

/**
 * @public
 */
export const getCurrentTheme = (): string => {
  // Light is the default for everyone (DESIGN.md §3.6). The old default,
  // 'theme-system', was written to localStorage on the first visit, so it is
  // moved to light once. An explicit Dark choice is kept.
  if (localStorage.getItem(lightDefaultKey) === null) {
    localStorage.setItem(lightDefaultKey, '1')
    if (localStorage.getItem('theme') === 'theme-system') {
      localStorage.setItem('theme', 'theme-light')
    }
  }
  return localStorage.getItem('theme') ?? getDefaultProps('theme', 'theme-light')
}
/**
 * @public
 */
export const getCurrentFontSize = (): string =>
  localStorage.getItem('fontsize') ?? getDefaultProps('fontsize', 'normal-font')
/**
 * @public
 */
export const getCurrentLanguage = (): string => {
  // Uzbek is this deployment's default. Anything untranslated falls back to
  // English per string (see loadTranslationsForComponent), so a partial
  // Uzbek locale degrades rather than breaking.
  const lang = localStorage.getItem('lang') ?? getDefaultProps('lang', 'uz')
  Analytics.setTag('language', lang)
  return lang
}
/**
 * @public
 */
export const getCurrentEmoji = (): string => localStorage.getItem('emoji') ?? getDefaultProps('emoji', 'emoji-system')

export class ThemeOptions {
  readonly variant: ThemeVariantType
  constructor (
    readonly fontSize: number,
    readonly dark: boolean,
    readonly language: string,
    readonly emoji: string
  ) {
    this.variant = dark ? ThemeVariant.Dark : ThemeVariant.Light
  }
}
export const themeStore = writable<ThemeOptions>()

// Default-locale dates follow the interface language (see dates.ts).
themeStore.subscribe((theme) => {
  if (theme !== undefined) setDateLanguage(theme.language)
})

export function initThemeStore (): void {
  themeStore.set(
    new ThemeOptions(
      getCurrentFontSize() === 'normal-font' ? 16 : 14,
      isThemeDark(getCurrentTheme()),
      getCurrentLanguage(),
      getCurrentEmoji()
    )
  )
}

export const languageStore = derived(themeStore, ($theme) => $theme?.language ?? '')
